import React, {
  useImperativeHandle,
  forwardRef,
  useState,
  Fragment,
  useEffect,
  useRef,
} from 'react';
import {
  View,
  Dimensions,
  TouchableOpacity,
  BackHandler,
  Animated,
  Platform,
  StatusBar,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Modal from 'react-native-modal';

import PropTypes from 'prop-types';

import { Icon } from '@vfgroup-oneplatform/foundation/Components';
import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';

import FrameworkTestKeys from '../../../../FrameworkTestKeys';
import { getThemeColors as getFrameworkThemeColors, getThemeImages } from '../../../../Themes';
import TrayLottie from '../trayLottie/TrayLottie';

import styles from './styles';

const { width, height } = Dimensions.get('window');

const TobiModal = forwardRef(({ onHideModal, children, trayIcon = null, trayType }, ref) => {
  const [isVisable, setIsVisable] = useState(false);
  const closeIconOpacity = useRef(new Animated.Value(1)).current;
  const [tobiVisable, setTobiVisable] = useState(false);
  const theme = useTheme();
  const foundationImages = getThemeImages(theme.name);
  const frameworkColors = getFrameworkThemeColors(theme.name);
  const isIOSDevice = Platform.OS === 'ios';

  useImperativeHandle(ref, () => ({
    showModal: withTobi => {
      if (withTobi) {
        setTobiVisable(true);
      }
      closeIconOpacityAnimation(1);
      setIsVisable(true);
    },
    hideModal: (withTobi, onHide) => {
      if (withTobi) {
        const delay = isIOSDevice ? 450 : 400;
        setTimeout(() => {
          setTobiVisable(false);
          onHide();
        }, delay);
      }
      closeIconOpacityAnimation(0);
      setIsVisable(false);
    },
  }));

  useEffect(() => {
    const back = BackHandler.addEventListener('hardwareBackPress', () => {
      if (isVisable) {
        hideModal();
        return true;
      }
      return false;
    });

    return () => {
      back.remove();
    };
  }, [isVisable]);

  const hideModal = () => {
    setIsVisable(false);
    onHideModal(tobiVisable);
  };

  const closeIconOpacityAnimation = value => {
    Animated.timing(closeIconOpacity, {
      toValue: value,
      duration: 200,
      useNativeDriver: true,
    }).start();
  };

  const renderCloseBtn = () => {
    return (
      <Animated.View
        style={[styles.close, { opacity: closeIconOpacity }]}
        testID={FrameworkTestKeys.trayTobiCloseAnimatedView}
      >
        <TouchableOpacity onPress={hideModal} testID={FrameworkTestKeys.TobiModalCloseBtn}>
          <Icon
            name={foundationImages.ic_close}
            type="image"
            size={25}
            testID={FrameworkTestKeys.trayTobiCloseIcon}
          />
        </TouchableOpacity>
      </Animated.View>
    );
  };
  return (
    <Fragment>
      {isVisable && <StatusBar translucent backgroundColor="transparent" />}
      <Modal
        isVisible={isVisable}
        coverScreen={false}
        animationInTiming={500}
        animationOutTiming={1000}
        deviceHeight={height}
        deviceWidth={width}
        backdropOpacity={0.7}
        style={styles.modal}
        backdropTransitionInTiming={500}
        // this solves the flicker issue when closing but doesn't affect the animation, for more details refer to this link: https://github.com/react-native-community/react-native-modal/issues/310
        backdropTransitionOutTiming={0}
        testID={FrameworkTestKeys.trayTobiModal}
      >
        <Animated.View
          style={[styles.tobiContainer(theme, tobiVisable)]}
          testID={FrameworkTestKeys.trayTobiModalAnimatedView}
        >
          {tobiVisable
            ? trayIcon || (
                <Icon
                  name={foundationImages.toBi}
                  type="image"
                  style={styles.tobiStyle}
                  testID={FrameworkTestKeys.trayTobiModalIcon}
                />
              )
            : null}
        </Animated.View>

        <View style={styles.container} testID={FrameworkTestKeys.trayTobiModalContainerView}>
          <View testID={FrameworkTestKeys.trayTobiModalView}>
            <TrayLottie isVisible={false} lottieStyle={styles.lottie} />
            {renderCloseBtn()}
          </View>
          <LinearGradient
            colors={frameworkColors.trayModalLinearGradient}
            style={styles.content}
            testID={FrameworkTestKeys.trayTobiModalLinearGradient}
          >
            {children}
          </LinearGradient>
        </View>
      </Modal>
    </Fragment>
  );
});

TobiModal.displayName = 'TobiModal';

TobiModal.propTypes = {
  onHideModal: PropTypes.func,
  children: PropTypes.object,
  trayIcon: PropTypes.element,
  trayType: PropTypes.string,
};

export default TobiModal;
